import { FileInfoDto, FileType } from '@lmt/shared-types';
import { AppTooltip } from '@lmt/unicorn-shared/app/components/common/ui-kit/tooltip/AppTooltip';
import { useFileCallbacks } from '@lmt/unicorn-shared/app/components/patients/profile/cases/components/documents/common';
import { useGetUserById } from '@lmt/unicorn-shared/app/features/users/selectors';
import { useScopedTranslation } from '@lmt/unicorn-shared/app/helpers/localization/useScopedTranslation';
import { format, isThisYear } from 'date-fns';
import React, { FC, useCallback } from 'react';
import { getDateTimeWithoutYear } from '../../../../common';

import styles from './VersionItem.module.scss';

export interface VersionItemProps {
  fileInfo: FileInfoDto;
  nameChangedFrom?: string;
  isCurrentVersion?: boolean;
  // If `undefined` or returns `false`, then `defaultOnClick` (using `useFileCallbacks`) is called
  onClick?: () => boolean;
}

export const VersionItem: FC<VersionItemProps> = (props) => {
  const { fileInfo, isCurrentVersion, nameChangedFrom, onClick } = props;
  const i18n = useScopedTranslation('patients.documents.versions');
  const { onClick: defaultOnClick } = useFileCallbacks(fileInfo);
  const userData = useGetUserById(fileInfo.authorId);

  const authorName = userData
    ? `${userData?.firstName ?? ''} ${userData?.lastName}`
    : i18n.t('unknown_author');

  const combinedOnClick = useCallback(async () => {
    if (fileInfo.isDeleted) {
      return;
    }
    // call defaultOnClick if onClick is undefined or returns false
    if (!onClick || !onClick()) {
      await defaultOnClick();
    }
  }, [onClick, defaultOnClick, fileInfo.isDeleted]);

  const isVersionOutdated = fileInfo.values?.['IsOutdated'];

  return (
    <div
      data-disabled={fileInfo.isDeleted}
      className={styles.container}
      onClick={combinedOnClick}
      data-test-id="version-list-item"
    >
      <div className={styles.divider}>
        <div
          className={styles.ellipse}
          data-state={isCurrentVersion ? 'active' : ''}
          data-disabled={fileInfo.isDeleted}
        ></div>
      </div>
      <div
        className={styles.versionNumberField}
        data-state={isCurrentVersion ? 'active' : ''}
        data-disabled={fileInfo.isDeleted}
      >
        {fileInfo.type == FileType.DocumentDraft
          ? i18n.t('draft')
          : i18n.t('version', { versionNumber: fileInfo.versionNumber })}
      </div>
      <div data-disabled={fileInfo.isDeleted} className={styles.dateField}>
        {isThisYear(fileInfo.uploadDateTime)
          ? getDateTimeWithoutYear(fileInfo.uploadDateTime)
          : format(fileInfo.uploadDateTime, 'PPp')}
      </div>
      <div className={styles.authorNameField}>{authorName}</div>
      {fileInfo.isDeleted ? (
        <div className={styles.newNameField}>{i18n.t('version_deleted')}</div>
      ) : isVersionOutdated ? (
        <div className={styles.outdatedField}>{i18n.t('version_outdated')}</div>
      ) : (
        nameChangedFrom && (
          <AppTooltip
            rootClassName={styles.newNameFieldTooltip}
            title={
              <div className={styles.newNameFieldTooltip}>
                {i18n.t('name_changed', {
                  oldName: nameChangedFrom,
                  newName: fileInfo.name,
                })}
              </div>
            }
          >
            <div className={styles.newNameField}>{i18n.t('new_name')}</div>
          </AppTooltip>
        )
      )}
    </div>
  );
};
